import React from 'react';

import { Box, Typography } from '@mui/material';

import {
  BEST_COMPETITOR_CHIP_STYLE,
  BEST_COMPETITOR_LABEL,
  LE_CHIP_STYLE,
  LE_SUPPLIER_LABEL,
  PARITY_FILTER_CHIP_BASE_SX,
  SUPPLIER_CHIP_COLORS,
} from './constants';

interface SupplierFilterChipsProps {
  suppliers: ReadonlyArray<string>;
  isActive: (supplier: string) => boolean;
  onToggle: (supplier: string) => void;
  label?: string;
  showLeChip?: boolean;
  showBestCompetitorChip?: boolean;
  isBestCompetitorActive?: boolean;
  onToggleBestCompetitor?: () => void;
}

const GREY_FALLBACK = { border: 'grey.400', bg: 'grey.100', color: 'text.primary' } as const;

export default function SupplierFilterChips({
  suppliers,
  isActive,
  onToggle,
  label,
  showLeChip = false,
  showBestCompetitorChip = false,
  isBestCompetitorActive = false,
  onToggleBestCompetitor,
}: SupplierFilterChipsProps) {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      {label && <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {suppliers.map((supplier) => {
          const chipStyle = SUPPLIER_CHIP_COLORS[supplier] ?? GREY_FALLBACK;
          const active = isActive(supplier);
          return (
            <Box
              key={supplier}
              component="button"
              type="button"
              aria-pressed={active}
              onClick={() => onToggle(supplier)}
              sx={{
                ...PARITY_FILTER_CHIP_BASE_SX,
                fontFamily: 'inherit',
                cursor: 'pointer',
                borderColor: chipStyle.border,
                bgcolor: active ? chipStyle.bg : 'transparent',
                color: chipStyle.color,
                opacity: active ? 1 : 0.5,
              }}
            >
              {active ? '✓ ' : ''}
              {supplier}
            </Box>
          );
        })}
        {showLeChip && (
          <Box
            component="span"
            sx={{
              ...PARITY_FILTER_CHIP_BASE_SX,
              cursor: 'default',
              borderColor: LE_CHIP_STYLE.border,
              bgcolor: LE_CHIP_STYLE.bg,
              color: LE_CHIP_STYLE.color,
            }}
          >
            ✓ {LE_SUPPLIER_LABEL}
          </Box>
        )}
        {showBestCompetitorChip && (
          <Box
            component="button"
            type="button"
            aria-pressed={isBestCompetitorActive}
            onClick={() => onToggleBestCompetitor?.()}
            sx={{
              ...PARITY_FILTER_CHIP_BASE_SX,
              fontFamily: 'inherit',
              cursor: 'pointer',
              borderColor: BEST_COMPETITOR_CHIP_STYLE.border,
              bgcolor: isBestCompetitorActive ? BEST_COMPETITOR_CHIP_STYLE.bg : 'transparent',
              color: BEST_COMPETITOR_CHIP_STYLE.color,
              opacity: isBestCompetitorActive ? 1 : 0.5,
            }}
          >
            {isBestCompetitorActive ? '✓ ' : ''}
            {BEST_COMPETITOR_LABEL}
          </Box>
        )}
      </Box>
    </Box>
  );
}
